import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IndividualChecklistService } from '../individual-checklist.service';
import ID from '../../../../types/id';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import ConsentChecklistTask from '../../../../interfaces/consent-checklist-task';
import { formatDate } from '@angular/common';
import Either from '../../../../types/either';
import {
  CreateChecklistTaskInput,
  UpdateChecklistTaskInput,
} from '../graphql/individual-checklist';
import { Observable, Subscription } from 'rxjs';
import Permission from '../../../../interfaces/permission';
import { checkPermission } from '../../../../pipes/permission.pipe';
import { DefaultSnackBarService } from '../../../../services/default-snack-bar.service';

type TaskEditDialogData = {
  isExisting: boolean;
  consentChecklistTask?: ConsentChecklistTask;
  consent_request_id: ID;
};

@Component({
    selector: 'consent-task-edit-dialog',
    templateUrl: './edit-dialog.component.html',
    styleUrls: ['./edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditDialogComponent {
  public isExisting: boolean = false;
  private task: ConsentChecklistTask;
  private consent_request_id: ID;
  private permissionSub: Subscription;
  public formGroup = new FormGroup({
    task: new FormControl<string | null>(null, [Validators.required]),
    consent_checklist_status_id: new FormControl<ID | null>(null, [
      Validators.required,
    ]),
    start_date: new FormControl<string | Date | null>(null),
    due_date: new FormControl<string | Date | null>(null),
    group_id: new FormControl<ID | null>(null),
    checklist_notes: new FormControl<string | null>(null),
    days_to_complete: new FormControl<number | null>(null),
  });

  constructor(
    public checklistService: IndividualChecklistService,
    public dialogRef: MatDialogRef<EditDialogComponent>,
    private _snackBar: DefaultSnackBarService,
    @Inject(MAT_DIALOG_DATA) dialogData: TaskEditDialogData
  ) {
    if (dialogData.isExisting) this.isExisting = dialogData.isExisting;
    if (dialogData.consentChecklistTask)
      this.task = dialogData.consentChecklistTask;
    this.consent_request_id = dialogData.consent_request_id;
  }
  private parseTask() {
    this.formGroup.setValue({
      task: this.task.task,
      consent_checklist_status_id: this.task.consent_checklist_status_id,
      start_date: this.task.start_date
        ? this.task.start_date + 'T00:00:00'
        : null,
      due_date: this.task.due_date ? this.task.due_date + 'T00:00:00' : null,
      group_id: this.task.group?.id ?? null,
      checklist_notes: this.task.checklist_notes,
      days_to_complete: this.task.days_to_complete,
    });
  }
  ngOnInit() {
    if (!this.isExisting) {
      let sub = this.checklistService.consentChecklistStatuses$.subscribe(
        (statuses) => {
          let status = statuses.find(
            (status) => status.checklist_status === 'Not Started'
          );
          if (status) {
            this.formGroup.controls.consent_checklist_status_id.setValue(
              status.id
            );
          }
          sub?.unsubscribe();
        }
      );
    } else {
      this.parseTask();
    }
    this.permissionSub = this.checklistService.permission$.subscribe(
      (permission) => {
        this.checkPermissions(permission);
      }
    );
  }
  private checkPermissions(permission: Permission) {
    for (let key of Object.keys(this.formGroup.controls)) {
      let allowed = checkPermission(permission, '_edit', key);
      if (!allowed) {
        this.formGroup.controls[
          key as keyof typeof this.formGroup.controls
        ].disable();
      }
    }
  }
  ngOnDestroy() {
    this.permissionSub?.unsubscribe();
  }
  private parseForm(): Either<
    CreateChecklistTaskInput,
    UpdateChecklistTaskInput
  > {
    let data = { ...this.formGroup.value };
    if (data.start_date) {
      data.start_date = formatDate(data.start_date, 'yyyy-MM-dd', 'en-US');
    }
    if (data.due_date) {
      data.due_date = formatDate(data.due_date, 'yyyy-MM-dd', 'en-US');
    }
    for (let [key, value] of Object.entries(this.formGroup.controls)) {
      if (value.disabled) {
        let dataKey = key as unknown as keyof typeof data;
        if (data.hasOwnProperty(dataKey)) {
          delete data[key as unknown as keyof typeof data];
        }
      }
    }
    return data as Either<CreateChecklistTaskInput, UpdateChecklistTaskInput>;
  }
  saveForm() {
    let input = this.parseForm();
    let obs: Observable<any>;
    if (this.isExisting && this.task) {
      obs = this.checklistService.updateTask(this.task.id, input);
    } else {
      input.consent_request_id = this.consent_request_id;
      obs = this.checklistService.createTask(input as CreateChecklistTaskInput);
    }
    obs.subscribe(() => {
      this.checklistService.refreshConsentChecklist(this.consent_request_id);
      this._snackBar.open('Task saved successfully');
      this.dialogRef.close();
    });
  }
}
